import Button from "@/Components/Button/Button";
import Modal, { ModalProps } from "@/Components/Modal/Modal";
import qrOptions from "@/Pages/Auth/Screens/TableScreen/qrOptions";
import { useSetModal } from "@/shared/atoms/useModal";
import { useSetTableScreenLayout } from "@/shared/atoms/useTableScreenLayout";
import useCommon from "@/shared/hooks/useCommon";
import useQrSettingsPut from "@/shared/mutations/qr/useQrSettingsPut";
import useQrSettingsQuery from "@/shared/queries/qr/useQrSettingsQuery";
import useRestaurantMapsWithTables from "@/shared/queries/restaurantMap/useRestaurantWithTables";
import { qrStatussessOptions } from "@/shared/utils/constant";
import { asArray } from "@/shared/utils/helpers";
import { QrSettings, RestaurantMap } from "@/shared/utils/interfaces";
import { Controller, useForm } from "react-hook-form";
import Select from "../Select/Select";

export interface TableModalProps extends ModalProps {}

const QrSettingsModal = ({ onExit, ...props }: TableModalProps) => {
  const { data: maps } = useRestaurantMapsWithTables();
  const { data } = useQrSettingsQuery();
  const setLayoutType = useSetTableScreenLayout();

  const { isPending, mutateAsync } = useQrSettingsPut();
  const setModal = useSetModal();
  const tCommon = useCommon();
  const { handleSubmit, control, formState } = useForm({
    defaultValues: {
      ...data,
      statuses: asArray(data?.statuses),
      map_ids: asArray(data?.map_ids) ?? undefined,
    },
  });

  const onSubmit = (values: QrSettings) => {
    return mutateAsync({ ...values, branch_id: data.branch_id }).then(() => {
      setModal("");
      setLayoutType(values.layout_type);
    });
  };

  return (
    <Modal width={"50rem"} onExit={onExit} {...props}>
      <div className="flex items-center">
        <h2>{tCommon("qrSettingsEdit")}</h2>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-[2rem]">
          <Controller
            name={`layout_type`}
            control={control}
            rules={{
              required: {
                value: true,
                message: tCommon("fieldRequired"),
              },
            }}
            render={({ onChange, value, name }) => (
              <Select
                label={tCommon("layoutType")}
                menuPlacement="bottom"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                getOptionLabel={(option) => tCommon(option.label)}
                menuPortalTarget={document.getElementById("root")}
                value={qrOptions.layout_types.find(
                  (type) => type.value === value
                )}
                onChange={(r) => onChange(r.value)}
                name={name}
                options={qrOptions.layout_types}
              />
            )}
          />
        </div>

        <div className="mb-[2rem]">
          <Controller
            name={`sort_option`}
            control={control}
            rules={{
              required: {
                value: true,
                message: tCommon("fieldRequired"),
              },
            }}
            render={({ onChange, value, name }) => (
              <Select
                label={tCommon("sortBy")}
                menuPlacement="bottom"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                getOptionLabel={(option) => tCommon(option.label)}
                menuPortalTarget={document.getElementById("root")}
                value={qrOptions.sort_options.find(
                  (type) => type.value === value
                )}
                onChange={(r) => onChange(r.value)}
                name={name}
                options={qrOptions.sort_options}
              />
            )}
          />
        </div>

        <div className="mb-[2rem]">
          <Controller
            name={`statuses`}
            control={control}
            rules={{
              required: {
                value: true,
                message: tCommon("fieldRequired"),
              },
            }}
            render={({ onChange, value, name }) => (
              <Select
                name={name}
                label={tCommon("reviews_page.status")}
                options={qrStatussessOptions}
                onChange={(value: any[]) =>
                  onChange(asArray(value)?.flatMap((v) => v?.value))
                }
                isMulti
                menuPlacement="bottom"
                value={qrStatussessOptions?.filter((method) =>
                  asArray(value)?.includes(method?.value[0])
                )}
                getOptionLabel={(option) =>
                  `${tCommon(`${option.translationKey}`)}`
                }
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                menuPortalTarget={document.getElementById("root")}
              />
            )}
          />
        </div>

        <div className="mb-[2rem]">
          <Controller
            name={`map_ids`}
            control={control}
            rules={{
              required: {
                value: true,
                message: tCommon("fieldRequired"),
              },
            }}
            render={({ onChange, value, name }) => (
              <Select
                error={asArray(formState.errors.map_ids)?.[0]?.message}
                name={name}
                isMulti
                label={tCommon("maps.map")}
                options={maps}
                onChange={(value: RestaurantMap[]) => {
                  onChange(
                    value.length === 0 ? undefined : value.map((v) => v.item_id)
                  );
                }}
                menuPlacement="bottom"
                value={asArray(maps).filter((option) =>
                  asArray(value).includes(option.item_id)
                )}
                getOptionLabel={(option) => option.name}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                menuPortalTarget={document.getElementById("root")}
              />
            )}
          />
        </div>

        <Button isPending={isPending} isDisabled={isPending}>
          {tCommon("save")}
        </Button>
      </form>
    </Modal>
  );
};

export default QrSettingsModal;
